import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { SelectOption } from '../../../model/selectOption'
import {
  ProjectStatus,
  WbsItemCodeFormat,
} from '../../../../domain/entity/ProjectEntity'
import {
  ReferencedEntity,
  ReferencedEntityWithIcon,
  REFERENCED_ENTITY_SEARCH_LIMIT,
} from '../../../../domain/value-object/ReferencedEntity'
import { findDivisions } from '../../../../infrastructure/api/divisions'
import { findBusinessClients } from '../../../../infrastructure/api/business-clients'
import { findUsers } from '../../../../infrastructure/api/users'

// 取得済みの全件リストを名前で絞り込む（オートコンプリート方式）。
// 共通のエンティティ検索上限件数までに丸める。
const filterByName = <T extends ReferencedEntity>(
  list: T[],
  text: string
): T[] => {
  const query = text.trim().toLowerCase()
  const filtered = query
    ? list.filter(entity => entity.name.toLowerCase().includes(query))
    : list
  return filtered.slice(0, REFERENCED_ENTITY_SEARCH_LIMIT)
}

// 部門 / 顧客 / ユーザーの全件を一度だけ取得し、それらに対するオートコンプリート
// 検索関数を公開する。あわせてステータス / 採番形式の固定 enum を選択肢として返す。
export const useProjectSelectOptions = () => {
  const intl = useIntl()
  const divisionsRef = useRef<ReferencedEntity[]>([])
  const businessClientsRef = useRef<ReferencedEntity[]>([])
  const usersRef = useRef<ReferencedEntityWithIcon[]>([])

  useEffect(() => {
    let ignore = false
    const fetch = async () => {
      try {
        const [divisions, businessClients, users] = await Promise.all([
          findDivisions(),
          findBusinessClients(),
          findUsers(),
        ])
        if (ignore) return
        divisionsRef.current = divisions.map(d => ({
          uuid: d.uuid,
          name: d.displayName,
        }))
        businessClientsRef.current = businessClients.map(b => ({
          uuid: b.uuid,
          name: b.displayName,
        }))
        usersRef.current = users.map(u => ({
          uuid: u.uuid,
          name: u.name,
          iconUrl: u.iconUrl,
        }))
      } catch (err) {
        // 取得失敗が未捕捉の rejection にならないよう握りつぶす（選択肢は空で描画）。
        console.error('Failed to load project select options.', err)
      }
    }
    fetch()
    return () => {
      ignore = true
    }
  }, [])

  const searchDivision = useCallback(
    async (text: string): Promise<ReferencedEntity[]> =>
      filterByName(divisionsRef.current, text),
    []
  )
  const searchBusinessClient = useCallback(
    async (text: string): Promise<ReferencedEntity[]> =>
      filterByName(businessClientsRef.current, text),
    []
  )
  const searchProjectManager = useCallback(
    async (text: string): Promise<ReferencedEntityWithIcon[]> =>
      filterByName(usersRef.current, text),
    []
  )

  const statusOptions = useMemo<SelectOption[]>(
    () => [
      {
        value: ProjectStatus.STANDBY,
        name: intl.formatMessage({ id: 'projectV2.status.standby' }),
      },
      {
        value: ProjectStatus.INPROGRESS,
        name: intl.formatMessage({ id: 'projectV2.status.inProgress' }),
      },
      {
        value: ProjectStatus.SUSPENDED,
        name: intl.formatMessage({ id: 'projectV2.status.suspended' }),
      },
      {
        value: ProjectStatus.COMPLETED,
        name: intl.formatMessage({ id: 'projectV2.status.completed' }),
      },
    ],
    [intl]
  )
  const wbsItemCodeFormatOptions = useMemo<SelectOption[]>(
    () => [
      {
        value: WbsItemCodeFormat.RANDOM,
        name: intl.formatMessage({ id: 'projectV2.wbsItemCodeFormat.random' }),
      },
      {
        value: WbsItemCodeFormat.SEQUENTIAL,
        name: intl.formatMessage({
          id: 'projectV2.wbsItemCodeFormat.sequential',
        }),
      },
    ],
    [intl]
  )

  return {
    searchDivision,
    searchBusinessClient,
    searchProjectManager,
    statusOptions,
    wbsItemCodeFormatOptions,
  }
}
